import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useEmployeeValesHooks } from '../vales/hooks';
import { EmployeeValeFormDialog } from '../vales/EmployeeValeFormDialog';
import { EmployeeValeParcelasDialog } from '../vales/EmployeeValeParcelasDialog';
import type { ValeStatus } from '../vales/types';
import type { ValeGlobalListItem } from './types';

export interface ValeAcoesDialogProps {
  open: boolean;
  /** Linha selecionada no grid global (id + employeeId + status + nomes). */
  vale: ValeGlobalListItem;
  onClose: () => void;
}

function statusChipColor(s: ValeStatus): 'default' | 'success' | 'warning' {
  if (s === 'PAG') return 'success';
  if (s === 'CAN') return 'default';
  return 'warning'; // ABE
}

function formatBRL(v: number): string {
  return (Number.isFinite(v) ? v : 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

function formatDate(iso: string): string {
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
}

/**
 * Pop-up "Ações" de UM vale específico (a partir do grid global). Traz o mesmo
 * conjunto do `EmployeeValesManager` — parcelas/baixa, editar (só ABE), cancelar
 * (só ABE) — reusando os diálogos e hooks do módulo por-colaborador (escopados
 * por `vale.employeeId`). Foca no vale selecionado, não relista todos do
 * colaborador. Espelho de `notas-fiscais-global/NotaFiscalAcoesDialog`.
 */
export function ValeAcoesDialog({ open, vale, onClose }: ValeAcoesDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const employeeId = vale.employeeId;

  const hooks = useEmployeeValesHooks(employeeId);
  // Detalhe fresco (DTO completo com vencimento1aParcela): necessário pros
  // diálogos de parcelas/editar, e regatea os botões após uma ação.
  const detailQuery = hooks.useDetail(open ? vale.id : null);
  const current = detailQuery.data ?? null;
  const status = current?.status ?? vale.status;
  const deleteMut = hooks.useDelete();

  const [parcelasOpen, setParcelasOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(false);

  const editable = status === 'ABE';
  const ready = current !== null;

  function invalidateGlobal(): void {
    qc.invalidateQueries({ queryKey: ['vales', 'global'] });
  }
  function handleClose(): void {
    invalidateGlobal();
    onClose();
  }

  async function confirmCancel(): Promise<void> {
    try {
      await deleteMut.mutateAsync(vale.id);
      toast.success(t('features.colaboradores.vales.toasts.canceled'));
      setPendingCancel(false);
      invalidateGlobal();
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const A = 'features.colaboradores.valesGlobal.acoes';

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t(`${A}.title`)}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="subtitle1">{vale.colaboradorNome}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`${A}.resumo`, {
                  valor: formatBRL(vale.valor),
                  parcelas: vale.nroParcelas,
                  data: formatDate(vale.dataSolicitacao),
                })}
              </Typography>
            </Box>
            <Box>
              <Chip
                size="small"
                label={t(`features.colaboradores.vales.status.${status}`)}
                color={statusChipColor(status)}
                variant={status === 'ABE' ? 'filled' : 'outlined'}
              />
            </Box>

            <Divider />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="contained"
                startIcon={<ReceiptIcon />}
                disabled={!ready}
                onClick={() => setParcelasOpen(true)}
              >
                {t(`${A}.parcelas`)}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                disabled={!ready || !editable}
                onClick={() => setEditing(true)}
              >
                {t(`${A}.editar`)}
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<CancelIcon />}
                disabled={!editable || deleteMut.isPending}
                onClick={() => setPendingCancel(true)}
              >
                {t(`${A}.cancelar`)}
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t(`${A}.gateHint`)}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <EmployeeValeParcelasDialog
        open={parcelasOpen}
        vale={current}
        hooks={hooks}
        onClose={() => {
          setParcelasOpen(false);
          invalidateGlobal();
        }}
      />

      {editing && current && (
        <EmployeeValeFormDialog
          open={editing}
          vale={current}
          hooks={{ useCreate: hooks.useCreate, useUpdate: hooks.useUpdate }}
          onClose={() => {
            setEditing(false);
            invalidateGlobal();
          }}
        />
      )}

      <ConfirmDialog
        open={pendingCancel}
        title={t('features.colaboradores.vales.cancelConfirm.title')}
        description={t('features.colaboradores.vales.cancelConfirm.description', { id: vale.id })}
        confirmLabel={t('features.colaboradores.vales.actions.confirmCancel')}
        loading={deleteMut.isPending}
        onConfirm={confirmCancel}
        onCancel={() => setPendingCancel(false)}
      />
    </>
  );
}
